import {
  ArrowRightOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  HistoryOutlined,
  RobotOutlined,
  TeamOutlined,
  UploadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Alert, Col, Row, Skeleton, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useDashboard } from "@/entities/dashboard";
import { errorMessage } from "@/shared/api";
import { ROUTES, SECTIONS } from "@/shared/config";
import { formatDate, formatDateTime, formatMoney } from "@/shared/lib";
import { ActionTag, EmptyState, PageHeader, SectionCard, StatCard } from "@/shared/ui";

/**
 * Сводка — ответ на единственный вопрос, с которым админку открывают: есть ли
 * работа. Поэтому очередь заявок стоит первой и ведёт прямо в свой раздел, а
 * денежные итоги — ниже.
 *
 * Плитки очереди подсвечиваются, только когда очередь не пуста: постоянно
 * жёлтые числа перестают читаться как сигнал уже к концу первого дня.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useDashboard();

  if (isError) return <Alert type="error" showIcon title={errorMessage(error)} />;
  if (isPending) return <Skeleton active />;

  const { counters, recent_actions, last_accrual_date } = data;

  return (
    <>
      <PageHeader
        section="dashboard"
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle")}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            color={SECTIONS.deposits.color}
            icon={<DownloadOutlined aria-hidden />}
            title={t("dashboard.depositsPending")}
            value={counters.deposits_pending}
            to={ROUTES.deposits}
            accent={counters.deposits_pending > 0}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            color={SECTIONS.withdrawals.color}
            icon={<UploadOutlined aria-hidden />}
            title={t("dashboard.withdrawalsPending")}
            value={counters.withdrawals_pending}
            to={ROUTES.withdrawals}
            accent={counters.withdrawals_pending > 0}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            color={SECTIONS.users.color}
            icon={<TeamOutlined aria-hidden />}
            title={t("dashboard.usersTotal")}
            value={counters.users_total}
            to={ROUTES.users}
            hint={
              counters.users_blocked > 0
                ? `${t("dashboard.usersBlocked")}: ${String(counters.users_blocked)}`
                : null
            }
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            color={SECTIONS.robots.color}
            icon={<RobotOutlined aria-hidden />}
            title={t("dashboard.userRobotsActive")}
            value={counters.user_robots_active}
            suffix={`/ ${String(counters.robots_active)}`}
          />
        </Col>

        <Col xs={24} md={12}>
          <StatCard
            color="#14b8a6"
            icon={<WalletOutlined aria-hidden />}
            title={t("dashboard.totalBalance")}
            value={formatMoney(counters.total_balance)}
          />
        </Col>
        <Col xs={24} md={12}>
          <StatCard
            color="#6366f1"
            icon={<DollarCircleOutlined aria-hidden />}
            title={t("dashboard.investedTotal")}
            value={formatMoney(counters.invested_total)}
          />
        </Col>

        <Col span={24}>
          <SectionCard
            color={SECTIONS.audit.color}
            icon={<HistoryOutlined aria-hidden />}
            title={t("dashboard.recentActions")}
            extra={
              <Typography.Text type="secondary">
                {t("dashboard.lastAccrual")}:{" "}
                {last_accrual_date ? formatDate(last_accrual_date) : t("dashboard.noAccruals")}
              </Typography.Text>
            }
            bodyPadding={0}
          >
            {/* Таблица, а не List: в antd 6 List объявлен устаревшим и уедет
                в следующем мажоре. Здесь он и не нужен — это те же три
                колонки журнала. */}
            <Table
              rowKey="id"
              pagination={false}
              showHeader={false}
              dataSource={recent_actions}
              locale={{
                emptyText: (
                  <EmptyState
                    icon={<HistoryOutlined aria-hidden />}
                    title={t("dashboard.emptyActions")}
                  />
                ),
              }}
              columns={[
                {
                  key: "action",
                  render: (_, row) => <ActionTag action={row.action} />,
                },
                {
                  key: "admin",
                  dataIndex: "admin_login",
                  width: 180,
                  render: (value: string) => (
                    <Typography.Text type="secondary">{value}</Typography.Text>
                  ),
                },
                {
                  key: "created_at",
                  width: 190,
                  align: "right",
                  render: (_, row) => (
                    <Typography.Text type="secondary">
                      {formatDateTime(row.created_at)}
                    </Typography.Text>
                  ),
                },
              ]}
            />
            <div
              style={{
                padding: "10px 14px",
                borderTop: "1px solid var(--ant-color-split)",
                textAlign: "right",
              }}
            >
              <Link to={ROUTES.audit}>
                {t("dashboard.allActions")} <ArrowRightOutlined aria-hidden />
              </Link>
            </div>
          </SectionCard>
        </Col>
      </Row>
    </>
  );
}
